import { useIsMobile, useIsRedactionActive } from '../../../../core'
import { useHasUserCapability } from '../../../hooks/useHasUserCapability'
import type { UserCapability, EnumValueMeta } from '@joyful/utils'
import { createEnumSchema } from '@joyful/utils'
import { parseAsStringEnum, useQueryState } from 'nuqs'
import { useState } from 'react'
import {
  EncounterAgentSessionQueryMap,
  LatestAgentChatConversationId,
} from './useAgentSessionTabs'
import {
  getEncounterSidebarDefaultViewOptions,
  getEncounterSidebarTabForDefaultView,
  isAgentLatestChatDefaultView,
  resolveEncounterSidebarDefaultView,
  useEncounterSidebarDefaultView,
} from './encounterSidebarDefaultView'

export type EncounterSidebarTabValue = 'action' | 'agent' | 'timeline'

interface EncounterSidebarTabMeta extends EnumValueMeta {
  capability?: UserCapability
}

export interface EncounterSidebarTab {
  label: string
  value: EncounterSidebarTabValue
}

export const EncounterSidebarTabValueSchema = createEnumSchema<
  EncounterSidebarTabValue,
  EncounterSidebarTabMeta
>([
  {
    title: 'Timeline',
    value: 'timeline',
  },
  {
    capability: 'agent',
    title: 'Agent',
    value: 'agent',
  },
  {
    capability: 'encounter.action_perform',
    title: 'Action',
    value: 'action',
  },
])

export function useEncounterSidebarTabs() {
  const hasCapability = useHasUserCapability()
  const isRedactionActive = useIsRedactionActive()

  return getEncounterSidebarTabs({
    hasCapability,
    isRedactionActive,
  })
}

export function getEncounterSidebarTabs({
  hasCapability,
  isRedactionActive,
}: {
  hasCapability: (capability: UserCapability) => boolean
  isRedactionActive: boolean
}) {
  return EncounterSidebarTabValueSchema.definitions.flatMap((definition) => {
    if (isRedactionActive && definition.value === 'action') {
      return []
    }

    if (
      definition.capability &&
      hasCapability(definition.capability) === false
    ) {
      return []
    }

    return [
      {
        label: definition.title,
        value: definition.value,
      },
    ]
  })
}

export const EncounterSidebarQueryMap = {
  sidebar: parseAsStringEnum([
    ...EncounterSidebarTabValueSchema.values(),
    'closed',
  ]),
}

/**
 * tracks and enforces type on sidebar tab query parameter
 */
export function useEncounterSidebarTab() {
  const isMobile = useIsMobile()
  const isRedactionActive = useIsRedactionActive()
  const hasCapability = useHasUserCapability()
  const isSidebarDefaultOpen = hasCapability('encounter.sidebar.default_open')
  const { storedDefaultView } = useEncounterSidebarDefaultView()
  const availableDefaultViews = getEncounterSidebarDefaultViewOptions({
    hasCapability,
    isRedactionActive,
  }).map((definition) => {
    return definition.value
  })
  const resolvedDefaultView = resolveEncounterSidebarDefaultView({
    availableDefaultViews,
    storedDefaultView,
  })
  const defaultOpenTab =
    getEncounterSidebarTabForDefaultView(resolvedDefaultView)
  const [sidebarTabParam, setSidebarTab] = useQueryState(
    'sidebar' satisfies keyof typeof EncounterSidebarQueryMap,
    EncounterSidebarQueryMap.sidebar,
  )
  const [conversationParam, setConversationParam] = useQueryState(
    'conversation' satisfies keyof typeof EncounterAgentSessionQueryMap,
    EncounterAgentSessionQueryMap.conversation,
  )
  const shouldOpenToStoredDefault =
    isSidebarDefaultOpen === true && isMobile === false
  const fallbackSidebarTab = shouldOpenToStoredDefault
    ? defaultOpenTab
    : 'closed'
  const [defaultOpenSnapshot, setDefaultOpenSnapshot] =
    useState<EncounterSidebarDefaultOpenSnapshot | null>(null)
  const nextDefaultOpenSnapshot = getEncounterSidebarDefaultOpenSnapshot({
    defaultOpenTab,
    selectLatestChat: isAgentLatestChatDefaultView(resolvedDefaultView),
    shouldOpenToStoredDefault,
    sidebarTabParam,
    snapshot: defaultOpenSnapshot,
  })

  if (nextDefaultOpenSnapshot !== defaultOpenSnapshot) {
    setDefaultOpenSnapshot(nextDefaultOpenSnapshot)
    if (nextDefaultOpenSnapshot !== null && sidebarTabParam === null) {
      void setSidebarTab(nextDefaultOpenSnapshot.tab)
      if (
        nextDefaultOpenSnapshot.selectLatestChat &&
        conversationParam === null
      ) {
        void setConversationParam(LatestAgentChatConversationId)
      }
    }
  }

  const sidebarTab = getVisibleEncounterSidebarTab({
    fallbackSidebarTab,
    sidebarTabParam,
    snapshot: nextDefaultOpenSnapshot,
  })
  const shouldSelectLatestAgentChat = shouldSelectLatestEncounterAgentChat({
    conversationParam,
    selectLatestChatFromSnapshot: nextDefaultOpenSnapshot?.selectLatestChat,
    sidebarTab,
    sidebarTabParam,
  })

  const openToDefaultView = () => {
    void setSidebarTab(defaultOpenTab)
    if (isAgentLatestChatDefaultView(resolvedDefaultView)) {
      void setConversationParam(LatestAgentChatConversationId)
      return
    }

    if (conversationParam === LatestAgentChatConversationId) {
      void setConversationParam(null)
    }
  }

  const setSidebarOpen = (isOpen: boolean) => {
    if (isOpen) {
      if (sidebarTab === 'closed') {
        openToDefaultView()
      }

      return
    }

    void setSidebarTab('closed')
  }

  return {
    hasOpenedSidebar: true,
    openToDefaultView,
    setSidebarOpen,
    setSidebarTab,
    shouldSelectLatestAgentChat,
    sidebarQueryParamConfig:
      EncounterSidebarQueryMap.sidebar.withDefault(fallbackSidebarTab),
    sidebarTab,
  }
}

interface EncounterSidebarDefaultOpenSnapshot {
  selectLatestChat: boolean
  tab: EncounterSidebarTabValue
}

function getEncounterSidebarDefaultOpenSnapshot({
  defaultOpenTab,
  selectLatestChat,
  shouldOpenToStoredDefault,
  sidebarTabParam,
  snapshot,
}: {
  defaultOpenTab: EncounterSidebarTabValue
  selectLatestChat: boolean
  shouldOpenToStoredDefault: boolean
  sidebarTabParam: EncounterSidebarTabValue | 'closed' | null
  snapshot: EncounterSidebarDefaultOpenSnapshot | null
}): EncounterSidebarDefaultOpenSnapshot | null {
  if (sidebarTabParam === 'closed') {
    return null
  }

  if (snapshot !== null) {
    return snapshot
  }

  if (sidebarTabParam !== null) {
    return null
  }

  if (shouldOpenToStoredDefault === false) {
    return null
  }

  return {
    selectLatestChat,
    tab: defaultOpenTab,
  }
}

function getVisibleEncounterSidebarTab({
  fallbackSidebarTab,
  sidebarTabParam,
  snapshot,
}: {
  fallbackSidebarTab: EncounterSidebarTabValue | 'closed'
  sidebarTabParam: EncounterSidebarTabValue | 'closed' | null
  snapshot: EncounterSidebarDefaultOpenSnapshot | null
}) {
  if (sidebarTabParam !== null) {
    return sidebarTabParam
  }

  if (snapshot !== null) {
    return snapshot.tab
  }

  return fallbackSidebarTab
}

function shouldSelectLatestEncounterAgentChat({
  conversationParam,
  selectLatestChatFromSnapshot,
  sidebarTab,
  sidebarTabParam,
}: {
  conversationParam: string | null
  selectLatestChatFromSnapshot: boolean | undefined
  sidebarTab: EncounterSidebarTabValue | 'closed'
  sidebarTabParam: EncounterSidebarTabValue | 'closed' | null
}) {
  if (sidebarTab !== 'agent') {
    return false
  }

  if (conversationParam === LatestAgentChatConversationId) {
    return true
  }

  if (sidebarTabParam !== null) {
    return false
  }

  if (conversationParam !== null) {
    return false
  }

  return selectLatestChatFromSnapshot === true
}
